import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import ReactTooltip from 'react-bootstrap/Tooltip';
import moment from 'moment';
import { user } from '../../../redux/user';
import Permissions from '../../../constants/Permissions';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import {
	useGetNotesQuery,
	useAddNoteMutation,
	useGetNoteCategoriesQuery
} from '../../../redux/managementCompanyWorkforce';
import Select from '../../../components/form/Select';
import { showNotification } from '../../../redux/notification';
import { setPreventNavigation } from '../../../redux/navigate';

const FlexProfessionalNotes = ({ managementCompanyId, caregiverId, caregiverName, isFetching }) => {
	const dispatch = useDispatch();
	const userData = useSelector(user);

	const [addFormActive, setAddFormActive] = useState(false);
	const [newNotes, setNewNotes] = useState('');
	const [selectedCategory, setSelectedCategory] = useState(null);

	const {
		isLoading: isLoadingCategories,
		isFetching: isFetchingCategories,
		data: categories
	} = useGetNoteCategoriesQuery({ managementCompanyId }, { refetchOnMountOrArgChange: true });
	const { isLoading: isLoadingNotes, data: notes } = useGetNotesQuery(
		{ managementCompanyId, caregiverId },
		{ refetchOnMountOrArgChange: true }
	);

	const [addNotes, { isLoading: loadingUpdate }] = useAddNoteMutation();

	const handleSelectCategory = ({ category }) => setSelectedCategory(categories.find((cat) => cat?.value === category));

	useEffect(() => {
		dispatch(setPreventNavigation(!!newNotes || selectedCategory));
	}, [newNotes, selectedCategory]);

	const handleCancel = () => {
		setAddFormActive(false);
		setNewNotes('');
		setSelectedCategory(null);
		dispatch(setPreventNavigation(false));
	};

	const handlePublish = async () => {
		try {
			await addNotes({
				managementCompanyId,
				caregiverId,
				body: {
					note: newNotes.trim(),
					category: selectedCategory.value
				}
			}).unwrap();
			dispatch(showNotification('success', `Your note for ${caregiverName} has been published.`));
			handleCancel();
		} catch (err) {
			dispatch(showNotification('error', `There was an issue completing this action. Please try again.`));
		}
	};

	if (isFetching || isFetchingCategories) return <LoadingSpinner />;

	return (
		<WithOnlyOnDesktop>
			<div className="flex-professional-notes">
				<div className="header-row">
					<div className="title">Notes</div>
					{!addFormActive && (
						<IsAuthorizedForManagementCompany
							exactPermission={Permissions.WORKFORCE_NOTES_EDIT}
							managementCompanyId={managementCompanyId}
						>
							{categories?.length ? (
								<Button className="btn add-note-button" onClick={() => setAddFormActive(true)}>
									+ Add note
								</Button>
							) : (
								<OverlayTrigger
									className="tooltip-component"
									placement="left"
									delay={{ show: 100, hide: 400 }}
									overlay={
										<ReactTooltip className="add-note-tooltip" id="info-tooltip" scrollHide={false} resizeHide={false}>
											<div className="tooltip-content">
												You cannot add a note as you do not have access to any note categories.
											</div>
										</ReactTooltip>
									}
								>
									<Button className="btn add-note-button-disabled" onClick={() => {}}>
										+ Add note
									</Button>
								</OverlayTrigger>
							)}
						</IsAuthorizedForManagementCompany>
					)}
				</div>
				{addFormActive && (
					<div className="add-note-form-container">
						<div className="notes-labels">
							<div>Add note</div>
							<div>
								({newNotes.length}/{userData.workforceNotesMaxChars})
							</div>
						</div>
						<textarea
							className="notes-textarea"
							maxLength={userData.workforceNotesMaxChars}
							value={newNotes}
							onChange={(e) => setNewNotes(e.target.value)}
						/>
						<div className="notes-footer">
							<Select
								className="category-select"
								handleChange={handleSelectCategory}
								name="category"
								options={categories}
								placeholder="Select a category"
								value={selectedCategory}
								disabled={isLoadingCategories}
								isSearchable={false}
							/>
							<div className="d-flex">
								<button type="button" onClick={handleCancel} className="btn-cancel">
									Cancel
								</button>
								<button
									type="button"
									disabled={
										!selectedCategory || newNotes.trim().length < userData.workforceNotesMinChars || loadingUpdate
									}
									className="btn publish-button"
									onClick={handlePublish}
								>
									Publish note
								</button>
							</div>
						</div>
					</div>
				)}
				{!isLoadingNotes && notes?.length > 0 && categories?.length > 0 && (
					<div className="notes-container">
						{notes.map((note) => (
							<div className="note" key={note.id}>
								<div className="note-header">
									<div className="category">{note.category}</div>
									<div className="d-flex">
										<div className="date">{moment(note.creation_date).format('MM/DD/YYYY - h:mm A')}</div>
										<div className="author">
											{note.added_by && (
												<>
													{!note.is_inactive && note.added_by}
													{note.is_current_user && <span className="current-user"> (you)</span>}
													{note.is_inactive && <span className="current-user">{note.added_by} (deactivated)</span>}
												</>
											)}
											{!note.added_by && <span className="current-user">(contact removed)</span>}
										</div>
									</div>
								</div>
								<div className="note-content">{note.content}</div>
							</div>
						))}
					</div>
				)}
				{!categories?.length && <div className="no-notes">You do not have permission to view any note categories.</div>}
				{categories?.length > 0 && !isLoadingNotes && !notes?.length && (
					<div className="no-notes">There are no notes for this professional.</div>
				)}
			</div>
		</WithOnlyOnDesktop>
	);
};

export default FlexProfessionalNotes;
